import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, BookOpen, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";

const pctCls = (p) => (p >= 85 ? "text-emerald-700 bg-emerald-50 border-emerald-200" : p >= 70 ? "text-sky-700 bg-sky-50 border-sky-200" : p >= 50 ? "text-amber-700 bg-amber-50 border-amber-200" : "text-rose-700 bg-rose-50 border-rose-200");

export default function PublisherCard({ studentId, studentName }) {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    if (!studentId) return;
    api.get(`/weekly-schedule/${studentId}/publisher-report`).then((r) => setRows(r.data)).catch((e) => toast.error(apiErr(e)));
  }, [studentId]);

  if (!rows) return <div className="py-8 text-center text-slate-500"><Loader2 className="animate-spin inline" size={20} /></div>;
  if (rows.length === 0) return <div className="card-ta p-8 text-center text-slate-500 text-sm">Program şablonundan atanmış ve sonucu girilmiş görev yok.</div>;

  return (
    <div className="space-y-4" data-testid="publisher-card">
      <div className="flex items-center justify-between gap-3 print:hidden">
        <div className="text-xs font-semibold text-slate-400 flex items-center gap-1.5"><BookOpen size={13} /> Program şablonlarındaki girilen sonuçlardan otomatik üretilir</div>
        <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="pubcard-print"><Printer size={14} /> <span className="ml-1">Yazdır</span></Button>
      </div>
      {rows.map((p) => (
        <div key={p.publisher} className="rounded-2xl border border-slate-200 overflow-hidden shadow-sm break-inside-avoid" data-testid={`pubcard-${p.publisher}`}>
          <div className="px-5 py-3 text-white flex flex-wrap items-center justify-between gap-2" style={{ background: "#0B1B3F" }}>
            <div>
              <div className="font-display font-extrabold tracking-wide uppercase">{p.publisher}</div>
              <div className="text-[11px] text-slate-300">{studentName ? `${studentName} · ` : ""}{p.tasks} görev · %{p.done_pct ?? 0} tamamlandı</div>
            </div>
            <div className="flex gap-4 text-center text-xs">
              <div><div className="text-[10px] text-slate-300">DOĞRU</div><div className="font-bold text-emerald-300">{p.correct}</div></div>
              <div><div className="text-[10px] text-slate-300">YANLIŞ</div><div className="font-bold text-rose-300">{p.wrong}</div></div>
              <div><div className="text-[10px] text-slate-300">BOŞ</div><div className="font-bold">{p.blank}</div></div>
              <div><div className="text-[10px] text-slate-300">NET</div><div className="font-bold text-amber-300">{p.net.toFixed(2)}</div></div>
              <div><div className="text-[10px] text-slate-300">BAŞARI</div><div className="font-bold">%{p.success_pct}</div></div>
            </div>
          </div>
          <div className={`px-5 py-2 text-xs font-semibold border-b ${pctCls(p.success_pct)}`} data-testid={`pubcard-comment-${p.publisher}`}>Yorum: {p.comment}</div>
          <table className="w-full text-xs">
            <thead><tr className="text-left text-slate-500 bg-slate-50 border-b border-slate-200"><th className="px-4 py-2">Kaynak / Ünite</th><th className="px-2 py-2">Ders</th><th className="px-2 py-2 text-center">Hafta</th><th className="px-2 py-2 text-right">D</th><th className="px-2 py-2 text-right">Y</th><th className="px-2 py-2 text-right">B</th><th className="px-2 py-2 text-right">Net</th><th className="px-2 py-2 text-right">Başarı</th><th className="px-4 py-2">Yorum</th></tr></thead>
            <tbody>
              {p.resources.map((r) => (
                <ResourceRows key={r.resource_name} r={r} showHeader={p.resources.length > 1 || r.resource_name !== p.publisher} />
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

function ResourceRows({ r, showHeader }) {
  return (
    <>
      {showHeader && (
        <tr className="bg-slate-100/70 border-b border-slate-200 font-bold text-slate-800">
          <td className="px-4 py-1.5" colSpan={3}>{r.resource_name}</td>
          <td className="px-2 py-1.5 text-right text-emerald-700">{r.correct}</td><td className="px-2 py-1.5 text-right text-rose-700">{r.wrong}</td><td className="px-2 py-1.5 text-right">{r.blank}</td>
          <td className="px-2 py-1.5 text-right font-mono">{r.net.toFixed(2)}</td><td className="px-2 py-1.5 text-right">%{r.success_pct}</td><td className="px-4 py-1.5 font-normal text-slate-500">{r.comment}</td>
        </tr>
      )}
      {r.units.map((u) => (
        <tr key={`${u.subject}|${u.unit}`} className="border-b border-slate-100">
          <td className="px-4 py-1.5 pl-7 text-slate-800">{u.unit || "—"}</td>
          <td className="px-2 py-1.5 text-slate-500">{u.subject}</td>
          <td className="px-2 py-1.5 text-center text-slate-500">{u.weeks.join(", ")}</td>
          <td className="px-2 py-1.5 text-right text-emerald-700 font-semibold">{u.correct}</td>
          <td className="px-2 py-1.5 text-right text-rose-700 font-semibold">{u.wrong}</td>
          <td className="px-2 py-1.5 text-right text-slate-500">{u.blank}</td>
          <td className="px-2 py-1.5 text-right font-mono font-semibold">{u.net.toFixed(2)}</td>
          <td className="px-2 py-1.5 text-right"><span className={`px-1.5 py-0.5 rounded border font-bold ${pctCls(u.success_pct)}`}>%{u.success_pct}</span></td>
          <td className="px-4 py-1.5 text-slate-500">{u.comment}</td>
        </tr>
      ))}
    </>
  );
}
